
interface PitchDiagramProps {
  positions: string[]
  mainPosition?: string
}

type PitchPoint = { x: number; y: number; label: string }

const POSITION_MAP: Record<string, PitchPoint> = {
  GK: { x: 50, y: 92, label: 'GK' },
  LB: { x: 24, y: 78, label: 'LB' },
  CB: { x: 41, y: 78, label: 'CB' },
  CB2: { x: 59, y: 78, label: 'CB' },
  RB: { x: 76, y: 78, label: 'RB' },
  LWB: { x: 24, y: 66, label: 'LWB' },
  DM: { x: 50, y: 66, label: 'DM' },
  RWB: { x: 76, y: 66, label: 'RWB' },
  LM: { x: 24, y: 56, label: 'LM' },
  CM: { x: 42, y: 56, label: 'CM' },
  CM2: { x: 58, y: 56, label: 'CM' },
  RM: { x: 76, y: 56, label: 'RM' },
  LW: { x: 24, y: 36, label: 'LW' },
  AM: { x: 50, y: 42, label: 'AM' },
  RW: { x: 76, y: 36, label: 'RW' },
  SS: { x: 50, y: 28, label: 'SS' },
  CF: { x: 50, y: 20, label: 'CF' },
}

const POSITION_ALIASES: Record<string, string> = {
  CDM: 'DM',
  CAM: 'AM',
  CM_LEFT: 'CM',
  CM_RIGHT: 'CM2',
  CB_LEFT: 'CB',
  CB_RIGHT: 'CB2',
}

const normalizePosition = (position: string) => POSITION_ALIASES[position] || position

function PitchDiagram({ positions, mainPosition }: PitchDiagramProps) {
  return (
    <div className="relative w-full aspect-[2/3] bg-green-800 rounded-xl overflow-hidden shadow-inner border-2 border-green-900/20">
      {/* Pitch markings */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-30 pointer-events-none">
        <rect x="0" y="0" width="100" height="100" fill="none" stroke="white" strokeWidth="0.5" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="0.5" />
        <circle cx="50" cy="50" r="15" fill="none" stroke="white" strokeWidth="0.5" />
        {/* Goals */}
        <rect x="35" y="0" width="30" height="10" fill="none" stroke="white" strokeWidth="0.5" />
        <rect x="35" y="90" width="30" height="10" fill="none" stroke="white" strokeWidth="0.5" />
        {/* Penalty Areas */}
        <rect x="20" y="0" width="60" height="20" fill="none" stroke="white" strokeWidth="0.5" />
        <rect x="20" y="80" width="60" height="20" fill="none" stroke="white" strokeWidth="0.5" />
      </svg>

      {/* Position Markers */}
      <div className="absolute inset-0">
        {Object.entries(POSITION_MAP).map(([key, pos]) => {
          const isActive = positions.some((position) => normalizePosition(position) === key)
          const isMain = normalizePosition(mainPosition || '') === key

          if (!isActive && !isMain) return null

          return (
            <div
              key={key}
              className={`absolute -translate-x-1/2 -translate-y-1/2 group transition-all duration-300`}
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            >
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-lg
                  ${isMain 
                    ? 'bg-yellow-400 text-green-900 scale-125 ring-2 ring-white z-20' 
                    : 'bg-white/90 text-green-900 hover:bg-white z-10'}
                `}
              >
                {pos.label}
              </div>
              {isMain && (
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded text-[8px] font-bold text-white uppercase tracking-tighter">
                  Main Pos
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PitchDiagram
